import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { FavoriteDescription } from '../logic/favorites'

interface FavoriteDescriptionsState {
  /** Whose favorites these name (the app user's id), so another user's never show. */
  userId: string | null
  /** Each favorite's name and standing, by its address. */
  descriptions: Readonly<Record<string, FavoriteDescription>>
  /** Keeps the user's descriptions of these addresses only (none, for another user). */
  keepOnly: (userId: string, paths: readonly string[]) => void
  /** Puts in the user's descriptions; those unchanged are left as they are. */
  put: (userId: string, descriptions: readonly FavoriteDescription[]) => void
}

const sameDescription = (a: FavoriteDescription, b: FavoriteDescription) =>
  a.name === b.name &&
  a.status === b.status &&
  a.area === b.area &&
  a.isView === b.isView &&
  a.parts.length === b.parts.length &&
  a.parts.every((part, index) => part === b.parts[index])

/**
 * The Favorites menu's names, worked out in the background (useDescribeFavorites)
 * and kept on this device, so the menu shows them at once, even on a new visit,
 * while they're checked again behind it. Only a change is written (each write
 * saves the whole list).
 */
export const useFavoriteDescriptionsStore = create<FavoriteDescriptionsState>()(
  persist(
    (set, get) => ({
      userId: null,
      descriptions: {},
      keepOnly: (userId, paths) => {
        const state = get()
        if (state.userId !== userId) {
          set({ userId, descriptions: {} })
          return
        }
        const wanted = new Set(paths)
        const kept = Object.entries(state.descriptions).filter(([path]) => wanted.has(path))
        if (kept.length < Object.keys(state.descriptions).length) {
          set({ descriptions: Object.fromEntries(kept) })
        }
      },
      put: (userId, descriptions) => {
        const state = get()
        const current = state.userId === userId ? state.descriptions : {}
        const changed = descriptions.filter((description) => {
          const known = current[description.path]
          return !known || !sameDescription(known, description)
        })
        if (state.userId === userId && changed.length === 0) return
        const next = { ...current }
        for (const description of changed) next[description.path] = description
        set({ userId, descriptions: next })
      },
    }),
    {
      name: 'arvoke.sidebar.favoriteDescriptions',
      version: 1,
      partialize: (state) => ({ userId: state.userId, descriptions: state.descriptions }),
    }
  )
)
